import { ReactElement, useCallback } from 'react';
import ru from 'react-phone-input-2/lang/ru.json';
import { Formik, FormikValues } from 'formik';
import 'react-phone-input-2/lib/high-res.css';
import PhoneInput from 'react-phone-input-2';
import { v4 as uuidV4 } from 'uuid';
import classNames from 'classnames';
import './module.scss';

export type PhoneFieldProps<T extends FormikValues> = {
    id?: string;
    label?: string;
    placeholder?: string;
    autoFocus?: boolean;
    checkTouched?: boolean;
    description?: string;
} & ({ name?: string; formik?: never } | { name: keyof T; formik: Formik<T> });

export type PhoneFieldType = { raw: string; formatted: string };

export const PhoneField = <T extends FormikValues>(
    props: PhoneFieldProps<T>
): ReactElement => {
    const {
        id = uuidV4(),
        autoFocus = false,
        formik,
        name = id,
        checkTouched = true,
    } = props;

    const onChange = useCallback(
        (value: string, _d, _e, formattedValue: string): void => {
            if (!formik) return;

            formik
                .setFieldValue(
                    name.toString(),
                    { raw: value, formatted: formattedValue },
                    true
                )
                .then(() => {
                    formik.setFieldTouched(name.toString(), true);
                });
        },
        [formik, name]
    );

    const getValue = useCallback((): string | undefined => {
        if (!formik) return undefined;

        const value: PhoneFieldType | undefined =
            formik.values[name] ?? undefined;

        return value ? value.raw : '7';
    }, [formik, name]);

    const getIncorrect = useCallback((): boolean => {
        if (!formik) return false;

        if (checkTouched && !formik.getFieldMeta(name.toString()).touched)
            return false;

        return !!formik.errors[name];
    }, [formik, name, checkTouched]);

    return (
        <div className={classNames('form-field phone-field')}>
            {props.label && (
                <label
                    className={classNames('field-label', 'phone-field-label')}
                    htmlFor={id}
                >
                    {props.label}
                </label>
            )}
            <PhoneInput
                containerClass={'phone-field-input-container'}
                inputProps={{
                    id: id,
                    name: name.toString(),
                    autoFocus: autoFocus,
                }}
                inputClass={classNames('field-input', 'phone-field-input', {
                    incorrect: getIncorrect(),
                })}
                placeholder={props.placeholder}
                value={getValue()}
                onChange={onChange}
                country={'ru'}
                localization={ru}
                countryCodeEditable={false}
                disableDropdown={true}
            />
            {formik && formik.errors[name] ? (
                <div className={classNames('field-error', 'phone-field-error')}>
                    {formik.errors[name] as string}
                </div>
            ) : (
                <div
                    className={classNames(
                        'field-description',
                        'phone-field-description'
                    )}
                >
                    {props.description}
                </div>
            )}
        </div>
    );
};
